import { pageTitle } from '@palaccount/config';
import type { EmployeeResponse } from '@palaccount/contracts';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Amount, Ltr } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { useWarehouses } from '@/features/inventory/stock-api';
import { AuthorshipLine } from '@/features/master-data/authorship-line';
import { usePagedState, usePageRange } from '@/lib/paging';
import { useCan } from '@/lib/permissions';
import { useDocumentDate } from '@/lib/session';
import { cn } from '@/lib/utils';

import {
  useCreateEmployee,
  useEmployeePay,
  useJobRoles,
  usePagedEmployees,
  useShifts,
  useTerminateEmployee,
  useUpdateEmployee,
  useUpdateEmployeePay,
} from './api';
import { HrTabs } from './hr-tabs';

const DEPARTMENTS = ['finance', 'sales', 'operations', 'retail', 'hr', 'it'] as const;

/**
 * The 300px rail is a narrow column, not a table: 25 rows fill it twice over
 * and the default 50 would put a scrollbar inside a scrollbar.
 */
const EMPLOYEE_PAGE_SIZE = 25;

/**
 * HR-01 — Employee List: the list, the identification card, the Roles &
 * system access chips (one per ACTIVE tenant role — R11), contact, and the
 * pay card. THE PAY CARD RENDERS ONLY WHEN payroll.view ANSWERS (R15): the
 * general read carries no pay field at all, so there is nothing to hide —
 * the card appears when its own gated query succeeds and never otherwise.
 */
export function EmployeesPage() {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === 'ar';
  const canPayroll = useCan('payroll.view');

  /**
   * NFR-42 — the 300px rail is a page, and the page is in the URL. HR-01 §6's
   * search box is what makes that safe: `q` reaches SQL, so page 2 of a search
   * is a page of the MATCHES rather than the matches of page 2.
   */
  const paging = usePagedState(EMPLOYEE_PAGE_SIZE);
  const [q, setQ] = useState('');
  const {
    data,
    isPending,
    isError,
  } = usePagedEmployees(
    { q: q.trim() || undefined, includeTerminated: true },
    paging.page,
    paging.pageSize,
  );
  const employees = data?.employees;
  const range = usePageRange(data, paging.pageSize);
  const { data: jobRoles } = useJobRoles();
  const { data: shifts } = useShifts();
  const { data: warehouses } = useWarehouses();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * ERR-HR-01-06 / Q-HR-01-3 (adopted by delegated ruling A4): the server
   * answers a role switch-off with `roleRemovalWarnings` when the person was
   * the last holder or is named on an open document. Held per save; cleared
   * the moment another employee is selected or another chip is toggled, so a
   * warning never outlives the change it is about.
   */
  const [roleWarnings, setRoleWarnings] = useState<
    NonNullable<EmployeeResponse['roleRemovalWarnings']>
  >([]);

  const selected =
    employees?.find((e) => e.id === selectedId) ?? (adding ? null : (employees?.[0] ?? null));
  const pay = useEmployeePay(selected?.id ?? null, canPayroll);

  const createEmployee = useCreateEmployee();
  const updateEmployee = useUpdateEmployee();
  const updatePay = useUpdateEmployeePay();
  const terminateEmployee = useTerminateEmployee();

  useEffect(() => {
    document.title = pageTitle(t('hr.employeesTitle'));
  }, [t]);

  const nameOf = (employee: EmployeeResponse) => (ar ? employee.fullNameAr : employee.fullName);

  return (
    <div className="grid gap-4">
      <HrTabs />
      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        {/* The 300px employee list (HR-01 §4). */}
        <Card>
          <CardContent className="grid gap-1 p-2">
            <div className="flex items-center justify-between px-2 py-1">
              <span className="text-sm font-semibold">{t('hr.employeesTitle')}</span>
              <Gated permission="hr.create" reasonKey="perm.cannot.createEmployees">
                <Button
                  size="sm"
                  aria-label={t('hr.addEmployee')}
                  onClick={() => {
                    setAdding(true);
                    setSelectedId(null);
                  }}
                >
                  <Plus aria-hidden />
                </Button>
              </Gated>
            </div>
            {/* HR-01 §6's search box. `paging.change` around the setter is
                BR-DSH-09-19: narrowing the list returns to page 1, through the
                setter rather than an effect. */}
            <Input
              aria-label={t('hr.searchEmployees')}
              placeholder={t('hr.searchEmployees')}
              value={q}
              onChange={(event) => paging.change(setQ)(event.target.value)}
              className="mx-2 mb-1 h-9"
            />
            {isPending ? (
              <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
            ) : isError ? (
              <p role="alert" className="p-4 text-sm text-destructive">
                {t('common.loadFailed')}
              </p>
            ) : (employees ?? []).length === 0 ? (
              <p className="p-4 text-center text-sm text-muted-foreground">{t('hr.employeesEmpty')}</p>
            ) : (
              (employees ?? []).map((employee) => (
                <button
                  key={employee.id}
                  type="button"
                  onClick={() => {
                    setAdding(false);
                    setSelectedId(employee.id);
                    setRoleWarnings([]);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md border-s-2 border-transparent px-2 py-2 text-start hover:bg-accent',
                    selected?.id === employee.id && 'border-primary bg-accent',
                    // Terminated staff dim to just over half opacity (§4).
                    employee.status === 'terminated' && 'opacity-60',
                  )}
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-bold">
                    {employee.fullName
                      .split(' ')
                      .map((part) => part[0])
                      .slice(0, 2)
                      .join('')}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{nameOf(employee)}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {ar ? employee.jobTitleAr : employee.jobTitle}
                    </span>
                  </span>
                  <EmployeeStatusPill status={employee.status} />
                </button>
              ))
            )}
            {/* NFR-42 — the range, the total, and the two steps. */}
            {!isPending && !isError && (data?.total ?? 0) > 0 ? (
              <Pager
                page={paging.page}
                pages={range.pages}
                first={range.first}
                last={range.last}
                total={range.total}
                onPage={paging.setPage}
                label={t('hr.employeesTitle')}
              />
            ) : null}
          </CardContent>
        </Card>

        <div className="grid content-start gap-4">
          {error ? (
            <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </p>
          ) : null}
          {adding ? (
            <NewEmployeeCard
              onCancel={() => setAdding(false)}
              onSubmit={(body) => {
                setError(null);
                createEmployee.mutate(body as never, {
                  onSuccess: (employee) => {
                    setAdding(false);
                    setSelectedId(employee.id);
                  },
                  onError: (mutationError) => setError(mutationError instanceof Error ? mutationError.message : t('common.saveFailed')),
                });
              }}
            />
          ) : selected ? (
            <>
              {/* Identification */}
              <Card>
                <CardContent className="grid gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="me-auto text-lg font-bold">{nameOf(selected)}</h2>
                    <Ltr value={selected.code} className="font-mono text-sm text-muted-foreground" />
                    <EmployeeStatusPill status={selected.status} />
                  </div>
                  <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                    <Row label={t('hr.jobTitle')} value={ar ? selected.jobTitleAr : selected.jobTitle} />
                    <Row label={t('hr.department')} value={t(`hr.dept.${selected.department}`)} />
                    <Row label={t('hr.hireDate')} value={selected.hireDate} mono />
                    {selected.terminationDate ? (
                      <Row label={t('hr.terminationDate')} value={selected.terminationDate} mono />
                    ) : null}
                    <Row label={t('hr.phone')} value={selected.phone ?? '—'} mono />
                    <Row label={t('hr.email')} value={selected.email ?? '—'} mono />
                    <Row label={t('hr.nationalId')} value={selected.nationalId ?? '—'} mono />
                    <Row
                      label={t('hr.manager')}
                      value={
                        employees?.find((e) => e.id === selected.managerId)
                          ? nameOf(employees.find((e) => e.id === selected.managerId)!)
                          : '—'
                      }
                    />
                    <Row
                      label={t('hr.shift')}
                      value={shifts?.find((s) => s.id === selected.shiftId)?.name ?? '—'}
                    />
                  </dl>
                  {/* Wave 4D — the rate a sales document naming this rep takes. */}
                  <DefaultCommissionEditor
                    key={selected.id}
                    value={selected.defaultCommissionPct}
                    disabled={selected.status === 'terminated' || updateEmployee.isPending}
                    onSave={(defaultCommissionPct) => {
                      setError(null);
                      updateEmployee.mutate(
                        { employeeId: selected.id, body: { defaultCommissionPct } },
                        {
                          onError: (mutationError) =>
                            setError(mutationError instanceof Error ? mutationError.message : t('common.saveFailed')),
                        },
                      );
                    }}
                  />
                  {/* A terminated employee cannot be terminated again — a
                      record's own state, not a permission (HR-01 §5). */}
                  {selected.status !== 'terminated' ? (
                    <TerminatePanel
                      onTerminate={(date) => {
                        setError(null);
                        terminateEmployee.mutate(
                          { employeeId: selected.id, terminationDate: date },
                          {
                            onError: (mutationError) =>
                              setError(mutationError instanceof Error ? mutationError.message : t('common.saveFailed')),
                          },
                        );
                      }}
                    />
                  ) : null}
                  {/*
                    MD-10's record. These four facts are NOT pay fields, so
                    R15's payroll gate does not reach them — who edited a
                    record is not what that person is paid, and this card is
                    the `hr.view` one.
                  */}
                  <AuthorshipLine record={selected} />
                </CardContent>
              </Card>

              {/* Roles & system access — one chip per ACTIVE role (R11). */}
              <Card>
                <CardContent className="grid gap-3">
                  <h3 className="text-sm font-semibold">{t('hr.rolesCard')}</h3>
                  <p className="rounded-md bg-primary/10 p-2 text-xs">{t('hr.rolesHint')}</p>
                  <div className="flex flex-wrap gap-2">
                    {(jobRoles ?? []).map((role) => {
                      const on = selected.jobRoleIds.includes(role.id);
                      return (
                        // The chip used to be `disabled` on `hr.edit` with
                        // nothing said — greyed and silent, which 16.7 rules
                        // out as firmly as hidden. Terminated stays a RECORD
                        // state and keeps its own plain disable.
                        <Gated
                          key={role.id}
                          permission="hr.edit"
                          reasonKey="perm.cannot.assignJobRoles"
                        >
                        <button
                          type="button"
                          disabled={selected.status === 'terminated'}
                          title={role.consequence ?? undefined}
                          onClick={() => {
                            const next = on
                              ? selected.jobRoleIds.filter((id) => id !== role.id)
                              : [...selected.jobRoleIds, role.id];
                            setRoleWarnings([]);
                            updateEmployee.mutate(
                              { employeeId: selected.id, body: { jobRoleIds: next } },
                              {
                                // ERR-HR-01-06: switching a role ON can never
                                // warn; switching one OFF answers with the
                                // A4 warning when somebody was relying on it.
                                onSuccess: (updated) =>
                                  setRoleWarnings(updated.roleRemovalWarnings ?? []),
                                onError: (mutationError) =>
                                  setError(mutationError instanceof Error ? mutationError.message : t('common.saveFailed')),
                              },
                            );
                          }}
                          className={cn(
                            'rounded-full border px-3 py-1 text-sm transition-colors disabled:opacity-50',
                            on
                              ? 'border-primary bg-primary/10 font-medium text-foreground'
                              : 'border-border text-muted-foreground hover:bg-accent',
                          )}
                        >
                          {on ? '✓ ' : ''}
                          {ar ? role.nameAr : role.name}
                        </button>
                        </Gated>
                      );
                    })}
                  </div>
                  {/*
                    ERR-HR-01-06 — the warning the prototype never gave.
                    Q-HR-01-3's adopted default (ruling A4): warn when the
                    switched-off role loses its last holder, or when open
                    documents still name this person through it. Amber, not
                    red — the change has been SAVED; this is consequence, not
                    refusal, and the fix (re-tick the chip, or re-route the
                    documents) stays one click away.
                  */}
                  {roleWarnings.length > 0 ? (
                    <div
                      role="alert"
                      data-testid="role-off-warning"
                      className="grid gap-1 rounded-md bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-400"
                    >
                      {roleWarnings.map((warning) => {
                        const roleName = ar ? warning.nameAr : warning.name;
                        return (
                          <p key={warning.jobRoleId}>
                            {warning.lastHolder
                              ? t('hr.roleOffLastHolder', { role: roleName })
                              : null}
                            {warning.lastHolder && warning.openDocuments > 0 ? ' ' : null}
                            {warning.openDocuments > 0
                              ? t('hr.roleOffOpenDocs', {
                                  role: roleName,
                                  count: warning.openDocuments,
                                })
                              : null}
                          </p>
                        );
                      })}
                    </div>
                  ) : null}
                  {/* The consequence lines under the chips (BR-HR-01-05). */}
                  {(jobRoles ?? [])
                    .filter((role) => selected.jobRoleIds.includes(role.id) && role.consequence)
                    .map((role) => (
                      <p key={role.id} className="text-xs text-muted-foreground">
                        → {ar ? role.nameAr : role.name}: {ar ? role.consequenceAr : role.consequence}
                      </p>
                    ))}
                  {/*
                    BR-HR-01-06 — "assigned warehouses must be shown only when
                    the Warehouse Manager role is on." The condition is the
                    SURFACE, not a seeded role name: tenant-made roles can
                    feed the warehouse-manager dropdown (BR-HR-01-05d), so
                    "the Warehouse Manager role is on" means "some assigned
                    role carries the warehouse_manager surface" — the same
                    reading the source-hint work gave BR-HR-01-16. Switching
                    the last such role off HIDES the block and deletes
                    nothing: the rows stay, dormant, exactly as the migration
                    header argues (HR-01 §5b).
                  */}
                  {(jobRoles ?? []).some(
                    (role) =>
                      selected.jobRoleIds.includes(role.id) &&
                      role.surfaces.includes('warehouse_manager'),
                  ) ? (
                    <div data-testid="assigned-warehouses" className="grid gap-2">
                      <h4 className="text-sm font-semibold">{t('hr.assignedWarehouses')}</h4>
                      {/*
                        BR-HR-01-23's reading, said where the list is edited:
                        an EMPTY list means every warehouse, not none. Without
                        this line an administrator clearing the last chip
                        reads the result as "may move nothing", which is the
                        exact inversion the spec warns about (HR-01 §5b).
                      */}
                      <p className="text-xs text-muted-foreground">
                        {t('hr.assignedWarehousesHint')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {(warehouses ?? [])
                          .filter(
                            (warehouse) =>
                              warehouse.isActive ||
                              selected.warehouseIds.includes(warehouse.id),
                          )
                          .map((warehouse) => {
                            const on = selected.warehouseIds.includes(warehouse.id);
                            return (
                              <Gated
                                key={warehouse.id}
                                permission="hr.edit"
                                reasonKey="perm.cannot.assignWarehouses"
                              >
                                <button
                                  type="button"
                                  disabled={selected.status === 'terminated'}
                                  onClick={() => {
                                    const next = on
                                      ? selected.warehouseIds.filter((id) => id !== warehouse.id)
                                      : [...selected.warehouseIds, warehouse.id];
                                    updateEmployee.mutate(
                                      { employeeId: selected.id, body: { warehouseIds: next } },
                                      {
                                        onError: (mutationError) =>
                                          setError(
                                            mutationError instanceof Error
                                              ? mutationError.message
                                              : t('common.saveFailed'),
                                          ),
                                      },
                                    );
                                  }}
                                  className={cn(
                                    'rounded-full border px-3 py-1 text-sm transition-colors disabled:opacity-50',
                                    on
                                      ? 'border-primary bg-primary/10 font-medium text-foreground'
                                      : 'border-border text-muted-foreground hover:bg-accent',
                                  )}
                                >
                                  {on ? '✓ ' : ''}
                                  {warehouse.name}
                                </button>
                              </Gated>
                            );
                          })}
                      </div>
                      {selected.warehouseIds.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          {t('hr.noWarehousesAssigned')}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              {/*
                BR-PERM-39's fence, and the one place in this sweep where the
                honest sentence is neither of the usual two.
                `payroll.view` IS a permission — so "only the Owner can see
                salaries" would be false — but it is one "held by the OWNER
                FLAG ALONE until the Owner grants it… nobody may grant a
                permission they do not hold themselves (BR-PERM-64)", so
                "ask your administrator" would be false too: an administrator
                cannot grant it. The notice says what is true and who to ask.

                The FIGURES are absent rather than blanked, because the server
                never sent them (R15 — the pay read 403s for hr.view). What
                BR-PERM-18 adds is that the person should still learn the card
                exists.
              */}
              {!canPayroll ? (
                <Card data-testid="pay-fence">
                  <CardContent className="grid gap-2">
                    <h3 className="text-sm font-semibold">{t('hr.payCard')}</h3>
                    <p className="rounded-md bg-amber-500/10 p-2 text-xs">
                      {t('perm.payrollFence')}
                    </p>
                  </CardContent>
                </Card>
              ) : null}
              {canPayroll && pay.data ? (
                <Card data-testid="pay-card">
                  <CardContent className="grid gap-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold">{t('hr.payCard')}</h3>
                      <Amount value={pay.data.netPay} currency={pay.data.currency} className="font-bold" />
                    </div>
                    <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                      <Row label={t('hr.baseSalary')} value={`${pay.data.baseSalary} ${pay.data.currency}`} mono />
                      <Row label={t('hr.payFrequency')} value={t(`hr.freq.${pay.data.payFrequency}`)} />
                      <Row label={t('hr.paymentMethod')} value={t(`hr.method.${pay.data.paymentMethod}`)} />
                    </dl>
                    {pay.data.lines.length > 0 ? (
                      <ul className="grid gap-1 text-sm">
                        {pay.data.lines.map((line) => (
                          <li key={line.id} className="flex justify-between">
                            <span>
                              {line.name}
                              {line.basis === 'percent' ? ` · ${t('unit.percent', { value: line.value })}` : ''}
                            </span>
                            <span
                              className={cn(
                                'font-mono',
                                line.kind === 'deduction' ? 'text-destructive' : 'text-primary',
                              )}
                            >
                              {line.kind === 'deduction' ? '−' : '+'}
                              {line.amount}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    <p className="rounded-md bg-primary/10 p-2 text-xs">
                      {t('hr.netPayFormula')}: <Ltr value={pay.data.netPay} className="font-mono font-bold" />
                    </p>
                    <Gated permission="payroll.edit" reasonKey="perm.cannot.editPay">
                      <PayEditor
                        key={selected.id}
                        baseSalary={pay.data.baseSalary}
                        paymentMethod={pay.data.paymentMethod}
                        onSave={(body) => {
                          setError(null);
                          updatePay.mutate(
                            { employeeId: selected.id, body },
                            {
                              onError: (mutationError) =>
                                setError(
                                  mutationError instanceof Error
                                    ? mutationError.message
                                    : t('common.saveFailed'),
                                ),
                            },
                          );
                        }}
                      />
                    </Gated>
                  </CardContent>
                </Card>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/40 py-1">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn('text-end', mono && 'font-mono')} dir="auto">
        {value}
      </dd>
    </div>
  );
}

export function EmployeeStatusPill({ status }: { status: EmployeeResponse['status'] }) {
  const { t } = useTranslation();
  const tone =
    status === 'active'
      ? 'bg-green-500/10 text-green-700 dark:text-green-400'
      : status === 'on_leave'
        ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
        : status === 'terminated'
          ? 'bg-destructive/10 text-destructive'
          : 'bg-muted text-muted-foreground';
  return (
    <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium', tone)}>
      {t(`hr.status.${status}`)}
    </span>
  );
}

function NewEmployeeCard({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState<string>('finance');
  // G-09: a new record's date starts as the company's today.
  const [hireDate, setHireDate] = useDocumentDate();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const complete = fullName.trim() !== '' && jobTitle.trim() !== '' && hireDate !== '';

  return (
    <Card>
      <CardContent className="grid gap-3">
        <h2 className="text-lg font-bold">{t('hr.addEmployee')}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1">
            <Label htmlFor="emp-name">{t('hr.fullName')}</Label>
            <Input id="emp-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="emp-title">{t('hr.jobTitle')}</Label>
            <Input id="emp-title" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="emp-dept">{t('hr.department')}</Label>
            <NativeSelect
              id="emp-dept"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
            >
              {DEPARTMENTS.map((dept) => (
                <option key={dept} value={dept}>
                  {t(`hr.dept.${dept}`)}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-1">
            <Label htmlFor="emp-hire">{t('hr.hireDate')}</Label>
            <Input
              id="emp-hire"
              type="date"
              value={hireDate}
              onChange={(e) => setHireDate(e.target.value)}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="emp-email">{t('hr.email')}</Label>
            <Input id="emp-email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="emp-phone">{t('hr.phone')}</Label>
            <Input id="emp-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
        </div>
        {/* ERR-HR-01-01's sentence sits under the disabled action. */}
        {!complete ? <p className="text-xs text-muted-foreground">{t('hr.employeeIncomplete')}</p> : null}
        <div className="flex gap-2">
          <Button
            disabled={!complete}
            onClick={() =>
              onSubmit({
                fullName,
                jobTitle,
                department,
                hireDate,
                ...(email ? { email } : {}),
                ...(phone ? { phone } : {}),
              })
            }
          >
            {t('common.save')}
          </Button>
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** The payroll.edit half of HR-01 §5d — salary and method, server-gated. */
function PayEditor({
  baseSalary,
  paymentMethod,
  onSave,
}: {
  baseSalary: string;
  paymentMethod: string;
  onSave: (body: { baseSalary: string; paymentMethod: 'bank_transfer' | 'cash' | 'check' }) => void;
}) {
  const { t } = useTranslation();
  const [salary, setSalary] = useState(baseSalary);
  const [method, setMethod] = useState(paymentMethod);
  const dirty = salary !== baseSalary || method !== paymentMethod;
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="grid gap-1">
        <Label htmlFor="pay-base">{t('hr.baseSalary')}</Label>
        <Input
          id="pay-base"
          value={salary}
          onChange={(e) => setSalary(e.target.value)}
          className="h-9 w-36 font-mono"
        />
      </div>
      <div className="grid gap-1">
        <Label htmlFor="pay-method">{t('hr.paymentMethod')}</Label>
        <NativeSelect
          id="pay-method"
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          className="h-9 w-40"
        >
          {(['bank_transfer', 'cash', 'check'] as const).map((key) => (
            <option key={key} value={key}>
              {t(`hr.method.${key}`)}
            </option>
          ))}
        </NativeSelect>
      </div>
      <Button
        size="sm"
        variant="outline"
        disabled={!dirty || !/^\d{1,13}(\.\d{1,2})?$/.test(salary)}
        onClick={() => onSave({ baseSalary: salary, paymentMethod: method as never })}
      >
        {t('common.save')}
      </Button>
    </div>
  );
}

function TerminatePanel({ onTerminate }: { onTerminate: (date: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // G-09: the leaving date starts as the company's today.
  const [date, setDate] = useDocumentDate();

  if (!open) {
    return (
      <div>
        <Gated permission="hr.edit" reasonKey="perm.cannot.terminateEmployees">
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            {t('hr.recordLeaver')}
          </Button>
        </Gated>
      </div>
    );
  }
  return (
    <div className="grid gap-2 rounded-md border border-destructive/40 p-3">
      <Label htmlFor="term-date" className="text-destructive">
        {t('hr.terminationDate')}
      </Label>
      <Input id="term-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      <div className="flex gap-2">
        <Button variant="destructive" size="sm" disabled={date === ''} onClick={() => onTerminate(date)}>
          {t('hr.confirmTerminate')}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Wave 4D — the rep's default sales commission %: what a new quotation,
 * order or invoice naming this person takes when nobody types a rate
 * (apps/api/src/platform/commission.ts). Blank clears it.
 */
function DefaultCommissionEditor({
  value,
  disabled,
  onSave,
}: {
  value: string | null;
  disabled: boolean;
  onSave: (pct: string | null) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(value ?? '');
  const typed = text.trim();
  const changed = typed !== (value ?? '');
  const valid = typed === '' || /^\d{1,3}(\.\d{1,2})?$/.test(typed);
  return (
    <div className="grid gap-1.5">
      <Label htmlFor="emp-commission">{t('hr.defaultCommissionPct')}</Label>
      <div className="flex gap-2">
        <Input
          id="emp-commission"
          dir="ltr"
          inputMode="decimal"
          className="w-32 font-mono"
          value={text}
          onChange={(event) => setText(event.target.value)}
          disabled={disabled}
          aria-invalid={!valid || undefined}
        />
        <Gated permission="hr.edit" reasonKey="perm.cannot.editEmployees">
          <Button
            variant="outline"
            size="sm"
            className="h-12"
            disabled={disabled || !changed || !valid}
            onClick={() => onSave(typed === '' ? null : typed)}
          >
            {t('common.save')}
          </Button>
        </Gated>
      </div>
    </div>
  );
}
